import { useState, useEffect } from 'react';
import type { IAsset } from '../providers';
import { captionChangedBody, type SystemMessage } from '../echo/systemMessages';

export interface UseAssetNameEditResult {
  editedName: string;
  isLoadingName: boolean;
  handleNameChange: (newName: string) => void;
  handleSaveName: () => Promise<void>;
  isSuccess: boolean;
  error: string | null;
}

/**
 * Hook for managing asset name editing with auto-save on blur
 * Handles state management and API calls for renaming assets
 *
 * @param asset - The asset to edit (can be null)
 * @returns Object containing state and handler functions
 */
export function useAssetNameEdit(
  asset: IAsset | null,
  postSystemComment?: (assetId: string, message: SystemMessage) => void,
): UseAssetNameEditResult {
  const [editedName, setEditedName] = useState<string>('');
  const [isLoadingName, setIsLoadingName] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize edited name when asset changes
  useEffect(() => {
    if (!asset) {
      setEditedName('');
      return;
    }
    setEditedName(asset.getName());
  }, [asset]);

  const handleNameChange = (newName: string) => {
    setEditedName(newName);
  };

  const handleSaveName = async () => {
    if (!asset || !editedName.trim()) return;

    const originalName = asset.getName();
    if (editedName === originalName) return; // No change

    setIsLoadingName(true);
    try {
      await asset.setName(editedName);
      setIsSuccess(true);
      setError(null);
      postSystemComment?.(asset.getId(), captionChangedBody(originalName, editedName));
    } catch (error) {
      console.error('Failed to update asset name:', error);
      setEditedName(originalName); // Revert on error
      setIsSuccess(false);
      setError('Failed to update asset name');
    } finally {
      setIsLoadingName(false);
    }
  };

  return {
    editedName,
    isLoadingName,
    handleNameChange,
    handleSaveName,
    isSuccess,
    error,
  };
}
